import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailRow, DetailRows, withStyles } from 'vopay';
import { BodyText, Card, Title } from 'UI';
import { pickDetail, renderDetailValue } from '../DetailValue/detailValue';
import contactIcon from './contact_user.svg';
import styles from './ContactCard.module.scss';

const cn = withStyles(styles);

export type ContactRole = 'primary' | 'billing' | 'technical' | 'support';

/** One contact set, flattened from whichever route returned it. */
export type Contact = {
  fullName: string;
  /** Only the partner routes return it; the row is rendered when `withOccupation` is set. */
  occupation?: string;
  title: string;
  email: string;
  phoneNumber: string;
};

type ContactCardProps = {
  role: ContactRole;
  /**
   * The two Figma scales a details tab is drawn at: `sm` is the account page (Body/13/Medium name
   * and role, 13px rows), `rg` the partner page (H5/Title 17 name, Body/16 role and rows).
   */
  size: 'sm' | 'rg';
  /** Undefined while the contact's source loads, null when that request failed. */
  contact: Contact | null | undefined;
  withOccupation?: boolean;
  /** What a blank field reads as instead of a dash, in the muted placeholder colour. */
  blankPlaceholder?: string;
  /**
   * Edit mode (the account page's ID-13964 D1): the heading becomes `title` and the rows give way to
   * the contact's inputs. The host owns the form and its copy.
   */
  edit?: { title: string; fields: ReactNode };
};

/** One of the four contact cards on a details tab (spec 5.6.1), read-only or being edited. */
export default function ContactCard({
  role,
  size,
  contact,
  withOccupation = false,
  blankPlaceholder,
  edit,
}: ContactCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.ContactCard' });

  const roleLabels: Record<ContactRole, string> = {
    primary: t('primary', { defaultValue: 'Primary Contact' }),
    billing: t('billing', { defaultValue: 'Billing Contact' }),
    technical: t('technical', { defaultValue: 'Technical Contact' }),
    support: t('support', { defaultValue: 'Support Contact' }),
  };

  const toDetailRow = (label: string, pick: (current: Contact) => string | undefined, key: string): DetailRow => ({
    label,
    value: renderDetailValue(pickDetail(contact, pick), blankPlaceholder),
    'data-testid': `contact-${role}-${key}`,
  });

  const details: DetailRow[] = [
    ...(withOccupation
      ? [toDetailRow(t('occupation', { defaultValue: 'Occupation' }), (current) => current.occupation, 'occupation')]
      : []),
    toDetailRow(t('jobTitle', { defaultValue: 'Title' }), (current) => current.title, 'jobTitle'),
    toDetailRow(t('emailAddress', { defaultValue: 'Email Address' }), (current) => current.email, 'email'),
    toDetailRow(t('phoneNumber', { defaultValue: 'Phone Number' }), (current) => current.phoneNumber, 'phone'),
  ];

  const fullName = renderDetailValue(
    pickDetail(contact, (current) => current.fullName),
    blankPlaceholder,
  );

  return (
    <Card variant="section" className={cn('card')} data-testid={`contactCard-${role}`}>
      <div className={cn('head')}>
        <span className={cn('avatar')} aria-hidden="true">
          <img src={contactIcon} alt="" className={cn('avatarGlyph')} />
        </span>

        <div className={cn('name')}>
          {edit && size === 'sm' && (
            <BodyText size="sm" weight="md">
              {edit.title}
            </BodyText>
          )}

          {edit && size === 'rg' && (
            <Title level="5" className={cn('fullName')}>
              {edit.title}
            </Title>
          )}

          {!edit && size === 'sm' && (
            <>
              {/* Figma (account page): Body/13/Medium, twice; the role label in Text/Secondary75 */}
              <BodyText size="sm" weight="md" data-testid={`contactCard-${role}-name`}>
                {fullName}
              </BodyText>

              <BodyText size="sm" weight="md" className={cn('roleLabel')}>
                {roleLabels[role]}
              </BodyText>
            </>
          )}

          {!edit && size === 'rg' && (
            <>
              {/* Figma (partner page): H5/Title 17/Emphasized over Body/16/Regular */}
              <Title level="5" className={cn('fullName')} data-testid={`contactCard-${role}-name`}>
                {fullName}
              </Title>

              <BodyText>{roleLabels[role]}</BodyText>
            </>
          )}
        </div>
      </div>

      {edit ? (
        <div className={cn('rows')} data-testid={`contactCard-${role}-fields`}>
          {edit.fields}
        </div>
      ) : (
        <DetailRows size={size} className={cn('rows')} details={details} />
      )}
    </Card>
  );
}
